/**
 * Settings Page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { alpacaAPI, botAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import NotificationSettings from '../components/NotificationSettings';
import PriceAlerts from '../components/PriceAlerts';
import DCASettings from '../components/DCASettings';
import PasswordStrength, { isPasswordAcceptable } from '../components/PasswordStrength';
import TwoFactorSetup from '../components/TwoFactorSetup';
import {
  User, Key, Shield, ShieldCheck, Trash2, Eye, EyeOff,
  CheckCircle, AlertTriangle, Zap, Bot, Target, TrendingUp,
  Users, Star, BarChart3, Bell, Sliders, ExternalLink, RefreshCw
} from 'lucide-react';

const Settings = () => {
  const { user, profile, hasAlpacaKeys, saveAlpacaKeys, removeAlpacaKeys, logout, mfaEnforced } = useAuth();

  // Two-factor
  const [twofaEnabled, setTwofaEnabled] = useState(!!user?.mfaEnabled);
  const [show2faSetup, setShow2faSetup] = useState(false);
  const [showDisable2fa, setShowDisable2fa] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling, setDisabling] = useState(false);
  useEffect(() => { setTwofaEnabled(!!user?.mfaEnabled); }, [user?.mfaEnabled]);

  // Alpaca settings
  const [showAlpacaForm, setShowAlpacaForm] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [savingKeys, setSavingKeys] = useState(false);

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Bot settings
  const [botConfig, setBotConfig] = useState(null);
  const [loadingBotConfig, setLoadingBotConfig] = useState(true);
  const [savingBotConfig, setSavingBotConfig] = useState(false);

  // Tikr.com settings
  const [tikrConnected, setTikrConnected] = useState(false);
  const [tikrEmail, setTikrEmail] = useState('');
  const [tikrApiKey, setTikrApiKey] = useState('');
  const [showTikrForm, setShowTikrForm] = useState(false);
  const [tikrSettings, setTikrSettings] = useState({
    enabled: false,
    guruFollowMode: true,        // Auto-add guru holdings to consideration
    scoreBoostEnabled: true,      // Boost AI score for guru-held stocks
    scoreBoostAmount: 15,         // Points to add to AI score
    positionSizeBoost: true,      // Increase position size for guru stocks
    positionSizeMultiplier: 1.25, // 25% larger positions
    sellOnGuruExit: false,        // Consider selling if gurus exit
    minGurusHolding: 1,           // Minimum gurus holding before boost applies
    trackNewPositions: true,      // Track when gurus add new positions
    prioritizeHighConviction: true, // Prioritize stocks held by multiple gurus
  });

  // Fetch bot config
  const fetchBotConfig = useCallback(async () => {
    try {
      const res = await botAPI.getConfig();
      if (res.data?.config) {
        setBotConfig(res.data.config);
        // Load Tikr settings from config if present
        if (res.data.config.tikrSettings) {
          setTikrSettings(prev => ({ ...prev, ...res.data.config.tikrSettings }));
        }
        if (res.data.config.tikrEnabled) {
          setTikrConnected(true);
        }
      }
    } catch (err) {
      console.error('Failed to fetch bot config:', err);
    } finally {
      setLoadingBotConfig(false);
    }
  }, []);

  useEffect(() => {
    fetchBotConfig();
    // Load Tikr connection from localStorage
    const savedTikr = localStorage.getItem('paeru_tikr_connected');
    const savedTikrEmail = localStorage.getItem('paeru_tikr_email');
    if (savedTikr) {
      setTikrConnected(true);
      setTikrEmail(savedTikrEmail || '');
    }
  }, [fetchBotConfig]);

  const updateBotConfig = async (key, value) => {
    const newConfig = { ...botConfig, [key]: value };
    setBotConfig(newConfig);
    
    setSavingBotConfig(true);
    try {
      await botAPI.updateConfig({ [key]: value });
      toast.success('Setting saved');
    } catch (err) {
      toast.error('Failed to save setting');
    } finally {
      setSavingBotConfig(false);
    }
  };

  const saveTikrSettings = async () => {
    setSavingBotConfig(true);
    try {
      await botAPI.updateConfig({ 
        tikrEnabled: tikrConnected,
        tikrSettings: tikrSettings 
      });
      toast.success('Tikr settings saved');
    } catch (err) {
      toast.error('Failed to save Tikr settings');
    } finally {
      setSavingBotConfig(false);
    }
  };

  const handleTikrConnect = () => {
    if (!tikrEmail) {
      toast.error('Email is required');
      return;
    }
    setTikrConnected(true);
    localStorage.setItem('paeru_tikr_connected', 'true');
    localStorage.setItem('paeru_tikr_email', tikrEmail);
    setShowTikrForm(false);
    toast.success('Guru tracking enabled');
    saveTikrSettings();
  };

  const handleTikrDisconnect = () => {
    if (window.confirm('Disable guru tracking?')) {
      setTikrConnected(false);
      localStorage.removeItem('paeru_tikr_connected');
      localStorage.removeItem('paeru_tikr_email');
      setTikrEmail('');
      toast.success('Guru tracking disabled');
    }
  };

  const handleSaveAlpacaKeys = async () => {
    if (!apiKey || !secretKey) {
      toast.error('Both keys are required');
      return;
    }

    setSavingKeys(true);
    try {
      // Verify first
      await alpacaAPI.verifyCredentials(apiKey, secretKey, true);
      await saveAlpacaKeys(apiKey, secretKey, true);
      toast.success('Alpaca connected successfully!');
      setShowAlpacaForm(false);
      setApiKey('');
      setSecretKey('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid credentials');
    } finally {
      setSavingKeys(false);
    }
  };

  const handleRemoveAlpacaKeys = async () => {
    if (window.confirm('Are you sure you want to disconnect Alpaca?')) {
      const result = await removeAlpacaKeys();
      if (result.success) {
        toast.success('Alpaca disconnected');
      } else {
        toast.error('Failed to disconnect');
      }
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (!isPasswordAcceptable(newPassword, [user?.username, user?.email])) {
      toast.error('Choose a stronger password (10+ characters, not a common one).');
      return;
    }

    setChangingPassword(true);
    try {
      const { authAPI } = await import('../services/api');
      await authAPI.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDisable2fa = async () => {
    setDisabling(true);
    try {
      const { authAPI } = await import('../services/api');
      await authAPI.disable2fa(disablePassword);
      setTwofaEnabled(false);
      setShowDisable2fa(false);
      setDisablePassword('');
      toast.success(mfaEnforced
        ? '2FA disabled — you\'ll be asked to set it up again at next login (2FA is required).'
        : '2FA disabled');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to disable 2FA');
    } finally {
      setDisabling(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navigation 
        title="Settings" 
        subtitle="Manage your account and preferences"
      />
      
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">

      {/* Profile Section */}
      <div className="card p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-accent-blue/15 flex items-center justify-center">
            <User className="w-6 h-6 text-accent-blue" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold">Profile</h2>
            <p className="text-text-secondary text-sm">Your account information</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-text-muted text-sm">Username</label>
            <p className="font-mono font-medium">{user?.username}</p>
          </div>
          <div>
            <label className="text-text-muted text-sm">Display Name</label>
            <p className="font-medium">{user?.displayName || user?.username}</p>
          </div>
          <div>
            <label className="text-text-muted text-sm">Email</label>
            <p className="font-medium">{user?.email || 'Not set'}</p>
          </div>
          <div>
            <label className="text-text-muted text-sm">Member Since</label>
            <p className="font-medium">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
            </p>
          </div>
        </div>
      </div>

      {/* Alpaca Integration */}
      <div className="card p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-accent-purple/15 flex items-center justify-center">
              <Zap className="w-6 h-6 text-accent-purple" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold">Alpaca Integration</h2>
              <p className="text-text-secondary text-sm">Connect your trading account</p>
            </div>
          </div>
          <div className={`badge ${hasAlpacaKeys ? 'badge-green' : 'badge-red'}`}>
            {hasAlpacaKeys ? (
              <><CheckCircle className="w-3 h-3 mr-1" /> Connected</>
            ) : (
              <><AlertTriangle className="w-3 h-3 mr-1" /> Not Connected</>
            )}
          </div>
        </div>

        {hasAlpacaKeys && !showAlpacaForm ? (
          <div className="flex gap-3">
            <button
              onClick={() => setShowAlpacaForm(true)}
              className="btn-secondary flex-1"
            >
              Update Keys
            </button>
            <button
              onClick={handleRemoveAlpacaKeys}
              className="btn-secondary text-accent-red border-accent-red/30 hover:bg-accent-red/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Disconnect
            </button>
          </div>
        ) : showAlpacaForm ? (
          <div className="space-y-4">
            <div>
              <label className="block text-text-secondary text-sm mb-2">API Key ID</label>
              <input
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="PKHIFX..."
                className="input font-mono"
              />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Secret Key</label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder="••••••••••••••••"
                  className="input font-mono pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted"
                >
                  {showSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleSaveAlpacaKeys}
                disabled={savingKeys || !apiKey || !secretKey}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {savingKeys ? 'Connecting...' : 'Connect & Verify'}
              </button>
              <button
                onClick={() => {
                  setShowAlpacaForm(false);
                  setApiKey('');
                  setSecretKey('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAlpacaForm(true)}
            className="btn-primary w-full"
          >
            Connect Alpaca
          </button>
        )}

        <p className="mt-4 text-text-muted text-xs">
          Get your API keys from{' '}
          <a
            href="https://app.alpaca.markets/paper/dashboard/overview"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-blue hover:underline"
          >
            Alpaca Paper Trading Dashboard
          </a>
        </p>
      </div>

      {/* Bot Trading Settings */}
      <div className="card p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-accent-blue/15 flex items-center justify-center">
            <Bot className="w-6 h-6 text-accent-blue" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold">Bot Trading Settings</h2>
            <p className="text-text-secondary text-sm">Configure your trading limits</p>
          </div>
          {savingBotConfig && <RefreshCw className="w-4 h-4 animate-spin text-accent-blue ml-auto" />}
        </div>

        {loadingBotConfig ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-text-muted" />
          </div>
        ) : botConfig ? (
          <div className="space-y-6">
            {/* Max Positions - Main Feature */}
            <div className="p-4 bg-bg-tertiary rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <label className="font-semibold">Maximum Concurrent Positions</label>
                  <p className="text-text-muted text-sm">How many stocks the bot can hold at once</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={botConfig.maxPositions || 5}
                    onChange={(e) => updateBotConfig('maxPositions', parseInt(e.target.value) || 5)}
                    min={1}
                    max={50}
                    className="w-20 input font-mono text-center text-lg"
                  />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[5, 10, 15, 20, 25, 30].map(num => (
                  <button
                    key={num}
                    onClick={() => updateBotConfig('maxPositions', num)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                      botConfig.maxPositions === num
                        ? 'bg-accent-blue text-white'
                        : 'bg-bg-card text-text-secondary hover:bg-accent-blue/20'
                    }`}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => updateBotConfig('maxPositions', 50)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                    botConfig.maxPositions >= 50
                      ? 'bg-accent-red text-white'
                      : 'bg-bg-card text-text-secondary hover:bg-accent-red/20'
                  }`}
                >
                  50 (Max)
                </button>
              </div>
              {botConfig.maxPositions > 20 && (
                <div className="mt-3 p-2 bg-accent-yellow/10 border border-accent-yellow/20 rounded-lg">
                  <p className="text-xs text-accent-yellow flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    High position count increases risk. Ensure adequate capital.
                  </p>
                </div>
              )}
            </div>

            {/* Quick Settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Max Daily Trades</label>
                <input
                  type="number"
                  value={botConfig.maxDailyTrades || 10}
                  onChange={(e) => updateBotConfig('maxDailyTrades', parseInt(e.target.value) || 10)}
                  min={1}
                  max={100}
                  className="input font-mono"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-sm mb-2">Min AI Score</label>
                <input
                  type="number"
                  value={botConfig.minAiScore || 60}
                  onChange={(e) => updateBotConfig('minAiScore', parseInt(e.target.value) || 60)}
                  min={30}
                  max={90}
                  className="input font-mono"
                />
              </div>
            </div>

            <Link to="/admin" className="btn-secondary w-full flex items-center justify-center gap-2">
              <Sliders className="w-4 h-4" />
              Advanced Bot Settings
            </Link>
          </div>
        ) : (
          <p className="text-text-muted text-center py-4">Unable to load bot settings</p>
        )}
      </div>

      {/* Guru Holdings (SEC 13F) */}
      <div className="card p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-display text-lg font-semibold">Guru Holdings (SEC 13F)</h2>
              <p className="text-text-secondary text-sm">Track institutional investors & 13F filings</p>
            </div>
          </div>
          {tikrConnected ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-accent-green/15 rounded-lg">
                <CheckCircle className="w-4 h-4 text-accent-green" />
                <span className="text-accent-green text-sm">Connected</span>
              </div>
              <button onClick={handleTikrDisconnect} className="btn-secondary p-2 text-accent-red">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : null}
        </div>

        {tikrConnected ? (
          <div className="space-y-6">
            {/* Connection Info */}
            <div className="p-4 bg-accent-green/5 border border-accent-green/20 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2 h-2 rounded-full bg-accent-green animate-pulse" />
                <span className="font-semibold text-accent-green">Powered by SEC EDGAR (live)</span>
              </div>
              <p className="text-text-muted text-sm">Account: {tikrEmail || 'Connected'}</p>
            </div>

            {/* Master Toggle */}
            <div className="flex items-center justify-between p-4 bg-bg-tertiary rounded-xl">
              <div>
                <label className="font-semibold">Enable Tikr Bot Integration</label>
                <p className="text-text-muted text-sm">Let the bot use guru data for trading decisions</p>
              </div>
              <button
                onClick={() => {
                  const newValue = !tikrSettings.enabled;
                  setTikrSettings(prev => ({ ...prev, enabled: newValue }));
                }}
                className={`relative w-14 h-7 rounded-full transition-all ${
                  tikrSettings.enabled ? 'bg-accent-green' : 'bg-bg-card'
                }`}
              >
                <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${
                  tikrSettings.enabled ? 'left-8' : 'left-1'
                }`} />
              </button>
            </div>

            {tikrSettings.enabled && (
              <>
                {/* Guru Influence Settings */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-text-secondary flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Guru Influence on Trading
                  </h3>

                  {/* Score Boosting */}
                  <div className="p-4 border border-border-color rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="font-medium">AI Score Boost</label>
                        <p className="text-text-muted text-xs">Add points to AI score if gurus hold the stock</p>
                      </div>
                      <button
                        onClick={() => setTikrSettings(prev => ({ ...prev, scoreBoostEnabled: !prev.scoreBoostEnabled }))}
                        className={`relative w-12 h-6 rounded-full transition-all ${
                          tikrSettings.scoreBoostEnabled ? 'bg-accent-blue' : 'bg-bg-tertiary'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                          tikrSettings.scoreBoostEnabled ? 'left-7' : 'left-1'
                        }`} />
                      </button>
                    </div>
                    {tikrSettings.scoreBoostEnabled && (
                      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-border-color">
                        <div>
                          <label className="text-xs text-text-muted">Boost Amount</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={tikrSettings.scoreBoostAmount}
                              onChange={(e) => setTikrSettings(prev => ({ ...prev, scoreBoostAmount: parseInt(e.target.value) || 10 }))}
                              min={5}
                              max={30}
                              className="input font-mono text-sm py-1.5 w-20"
                            />
                            <span className="text-text-muted text-sm">points</span>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-text-muted">Min Gurus Holding</label>
                          <input
                            type="number"
                            value={tikrSettings.minGurusHolding}
                            onChange={(e) => setTikrSettings(prev => ({ ...prev, minGurusHolding: parseInt(e.target.value) || 1 }))}
                            min={1}
                            max={5}
                            className="input font-mono text-sm py-1.5"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Position Size Boost */}
                  <div className="p-4 border border-border-color rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <label className="font-medium">Position Size Boost</label>
                        <p className="text-text-muted text-xs">Larger positions for guru-held stocks</p>
                      </div>
                      <button
                        onClick={() => setTikrSettings(prev => ({ ...prev, positionSizeBoost: !prev.positionSizeBoost }))}
                        className={`relative w-12 h-6 rounded-full transition-all ${
                          tikrSettings.positionSizeBoost ? 'bg-accent-green' : 'bg-bg-tertiary'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                          tikrSettings.positionSizeBoost ? 'left-7' : 'left-1'
                        }`} />
                      </button>
                    </div>
                    {tikrSettings.positionSizeBoost && (
                      <div className="mt-3 pt-3 border-t border-border-color">
                        <label className="text-xs text-text-muted">Size Multiplier</label>
                        <div className="flex items-center gap-3 mt-1">
                          <input
                            type="range"
                            value={tikrSettings.positionSizeMultiplier * 100}
                            onChange={(e) => setTikrSettings(prev => ({ ...prev, positionSizeMultiplier: parseInt(e.target.value) / 100 }))}
                            min={100}
                            max={200}
                            step={5}
                            className="flex-1"
                          />
                          <span className="font-mono text-accent-green font-semibold w-16">
                            {Math.round(tikrSettings.positionSizeMultiplier * 100)}%
                          </span>
                        </div>
                        <p className="text-xs text-text-muted mt-1">
                          Guru stocks get {Math.round((tikrSettings.positionSizeMultiplier - 1) * 100)}% larger positions
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Priority & High Conviction */}
                  <div className="p-4 border border-border-color rounded-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="font-medium">Prioritize High Conviction</label>
                        <p className="text-text-muted text-xs">Rank stocks held by multiple gurus higher</p>
                      </div>
                      <button
                        onClick={() => setTikrSettings(prev => ({ ...prev, prioritizeHighConviction: !prev.prioritizeHighConviction }))}
                        className={`relative w-12 h-6 rounded-full transition-all ${
                          tikrSettings.prioritizeHighConviction ? 'bg-accent-purple' : 'bg-bg-tertiary'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                          tikrSettings.prioritizeHighConviction ? 'left-7' : 'left-1'
                        }`} />
                      </button>
                    </div>
                  </div>

                  {/* Sell Signal */}
                  <div className="p-4 border border-accent-red/20 rounded-xl bg-accent-red/5">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="font-medium text-accent-red">Sell on Guru Exit</label>
                        <p className="text-text-muted text-xs">Consider selling if tracked gurus are exiting</p>
                      </div>
                      <button
                        onClick={() => setTikrSettings(prev => ({ ...prev, sellOnGuruExit: !prev.sellOnGuruExit }))}
                        className={`relative w-12 h-6 rounded-full transition-all ${
                          tikrSettings.sellOnGuruExit ? 'bg-accent-red' : 'bg-bg-tertiary'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                          tikrSettings.sellOnGuruExit ? 'left-7' : 'left-1'
                        }`} />
                      </button>
                    </div>
                    {tikrSettings.sellOnGuruExit && (
                      <p className="mt-2 text-xs text-accent-yellow flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        13F filings are 45 days delayed. Use with caution.
                      </p>
                    )}
                  </div>

                  {/* New Position Tracking */}
                  <div className="p-4 border border-border-color rounded-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="font-medium">Track New Guru Positions</label>
                        <p className="text-text-muted text-xs">Add newly bought guru stocks to watchlist</p>
                      </div>
                      <button
                        onClick={() => setTikrSettings(prev => ({ ...prev, trackNewPositions: !prev.trackNewPositions }))}
                        className={`relative w-12 h-6 rounded-full transition-all ${
                          tikrSettings.trackNewPositions ? 'bg-accent-yellow' : 'bg-bg-tertiary'
                        }`}
                      >
                        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                          tikrSettings.trackNewPositions ? 'left-7' : 'left-1'
                        }`} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <button
                  onClick={saveTikrSettings}
                  disabled={savingBotConfig}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  {savingBotConfig ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Save Tikr Settings
                </button>

                {/* Example Box */}
                <div className="p-4 bg-accent-blue/10 border border-accent-blue/20 rounded-xl">
                  <h4 className="font-semibold text-accent-blue mb-2">How Tikr Integration Works</h4>
                  <div className="space-y-2 text-sm text-text-secondary">
                    <p>• <strong>Score Boost:</strong> AAPL has AI score 62% → Buffett holds it → Score becomes {62 + tikrSettings.scoreBoostAmount}%</p>
                    <p>• <strong>Position Size:</strong> Normal $1,000 trade → Guru stock → ${Math.round(1000 * tikrSettings.positionSizeMultiplier)} trade</p>
                    <p>• <strong>High Conviction:</strong> Stocks held by 3+ tracked gurus get analyzed first</p>
                  </div>
                </div>
              </>
            )}

            <Link to="/gurus" className="btn-secondary w-full flex items-center justify-center gap-2">
              <Star className="w-4 h-4" />
              Manage Tracked Gurus
            </Link>
          </div>
        ) : (
          <div>
            {showTikrForm ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-text-secondary text-sm mb-2">Notification Email (optional)</label>
                  <input
                    type="email"
                    value={tikrEmail}
                    onChange={(e) => setTikrEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm mb-2">API Key (Optional)</label>
                  <input
                    type="password"
                    value={tikrApiKey}
                    onChange={(e) => setTikrApiKey(e.target.value)}
                    placeholder="For premium features"
                    className="input"
                  />
                </div>
                <div className="flex gap-3">
                  <button onClick={handleTikrConnect} className="btn-primary flex-1">
                    Enable Guru Tracking
                  </button>
                  <button onClick={() => setShowTikrForm(false)} className="btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-bg-tertiary rounded-xl">
                    <Users className="w-8 h-8 text-accent-blue mx-auto mb-2" />
                    <p className="text-sm font-medium">Track 1000+ Funds</p>
                    <p className="text-xs text-text-muted">Hedge funds & institutions</p>
                  </div>
                  <div className="text-center p-4 bg-bg-tertiary rounded-xl">
                    <BarChart3 className="w-8 h-8 text-accent-purple mx-auto mb-2" />
                    <p className="text-sm font-medium">13F Analysis</p>
                    <p className="text-xs text-text-muted">Quarterly holdings data</p>
                  </div>
                  <div className="text-center p-4 bg-bg-tertiary rounded-xl">
                    <TrendingUp className="w-8 h-8 text-accent-green mx-auto mb-2" />
                    <p className="text-sm font-medium">Bot Integration</p>
                    <p className="text-xs text-text-muted">AI score boosting</p>
                  </div>
                </div>
                <button onClick={() => setShowTikrForm(true)} className="btn-primary w-full">
                  Enable Guru Tracking
                </button>
                <p className="mt-3 text-center text-text-muted text-xs">
                  Guru 13F holdings are sourced live from SEC EDGAR — free, no account needed.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Security Section */}
      <div className="card p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-accent-green/15 flex items-center justify-center">
            <Shield className="w-6 h-6 text-accent-green" />
          </div>
          <div>
            <h2 className="font-display text-lg font-semibold">Security</h2>
            <p className="text-text-secondary text-sm">Manage your password</p>
          </div>
        </div>

        {showPasswordForm ? (
          <div className="space-y-4">
            <div>
              <label className="block text-text-secondary text-sm mb-2">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
              />
              <PasswordStrength password={newPassword} userInputs={[user?.username, user?.email]} />
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">Confirm New Password</label>
              <input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="input"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !isPasswordAcceptable(newPassword, [user?.username, user?.email]) || newPassword !== confirmNewPassword}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </button>
              <button
                onClick={() => {
                  setShowPasswordForm(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmNewPassword('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowPasswordForm(true)}
            className="btn-secondary w-full"
          >
            <Key className="w-4 h-4 mr-2" />
            Change Password
          </button>
        )}
      </div>

      {/* Two-Factor Authentication */}
      <div className="card p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl bg-accent-purple/15 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-accent-purple" />
          </div>
          <div className="flex-1">
            <h2 className="font-display text-lg font-semibold">Two-Factor Authentication</h2>
            <p className="text-text-secondary text-sm">
              {twofaEnabled ? 'Extra protection is active on your account.' : 'Protect your account with an authenticator app.'}
            </p>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full ${twofaEnabled ? 'bg-accent-green/15 text-accent-green' : 'bg-accent-yellow/15 text-accent-yellow'}`}>
            {twofaEnabled ? 'Enabled' : 'Not enabled'}
          </span>
        </div>

        {show2faSetup ? (
          <TwoFactorSetup
            onComplete={() => { setShow2faSetup(false); setTwofaEnabled(true); toast.success('Two-factor authentication enabled'); }}
            onCancel={() => setShow2faSetup(false)}
          />
        ) : showDisable2fa ? (
          <div className="space-y-3">
            <p className="text-sm text-text-secondary">Enter your password to turn off 2FA.
              {mfaEnforced && <span className="text-accent-yellow"> Note: 2FA is required, so you'll be asked to set it up again next login.</span>}
            </p>
            <input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} placeholder="Your password" className="input" />
            <div className="flex gap-3">
              <button onClick={handleDisable2fa} disabled={disabling || !disablePassword} className="btn-secondary text-accent-red border-accent-red/30 hover:bg-accent-red/10 flex-1 disabled:opacity-50">
                {disabling ? 'Disabling...' : 'Disable 2FA'}
              </button>
              <button onClick={() => { setShowDisable2fa(false); setDisablePassword(''); }} className="btn-secondary">Cancel</button>
            </div>
          </div>
        ) : twofaEnabled ? (
          <button onClick={() => setShowDisable2fa(true)} className="btn-secondary w-full text-accent-red border-accent-red/30 hover:bg-accent-red/10">
            Disable Two-Factor
          </button>
        ) : (
          <button onClick={() => setShow2faSetup(true)} className="btn-primary w-full flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4" /> Enable Two-Factor
          </button>
        )}
      </div>

      {/* Danger Zone */}
      <div className="card p-6 mb-6 border-accent-red/30">
        <h2 className="font-display text-lg font-semibold text-accent-red mb-4">Danger Zone</h2>
        <p className="text-text-secondary text-sm mb-4">
          These actions are permanent and cannot be undone.
        </p>
        <button
          onClick={() => {
            if (window.confirm('Are you sure you want to sign out?')) {
              logout();
            }
          }}
          className="btn-secondary text-accent-red border-accent-red/30 hover:bg-accent-red/10"
        >
          Sign Out
        </button>
      </div>

      {/* Notifications */}
      <div className="mb-6"><NotificationSettings /></div>

      {/* Price Alerts */}
      <div className="mb-6"><PriceAlerts /></div>

      {/* DCA Settings */}
      <div className="mb-6"><DCASettings /></div>

      {/* Footer */}
      <div className="mt-8 text-center text-text-muted text-sm">
        <p>Paeru Exchange v1.0.0</p>
        <p className="mt-1">Your data is stored locally and never leaves your device.</p>
      </div>
      </div>
    </div>
  );
};

export default Settings;
